import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Stack,
  Typography,
  Icon,
  Box,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from 'investira.react.components';
import {
  buildComparadorFundosPath,
  COMPARADOR_FUNDOS_MAX_ITEMS
} from '../_shared/comparadorFundos.helpers';
import ComparadorFundosBuscarDialog from './ComparadorFundosBuscarDialog';
import ComparadorFundosHistory from './ComparadorFundosHistory';
import { PessoaImage } from '../../../components/molecules';
import utils from '../../../utils';
import {
  deleteComparadorFundosHistoryItems,
  readComparadorFundosHistory,
  saveComparadorFundosHistoryItem,
  writeComparadorFundosHistory
} from '../_shared/comparadorFundos.history';

/**
 * Resume o estado atual da selecao para manter o contexto curto do cabecalho.
 *
 * @param {Object[]} pComparisonFundos Fundos selecionados já normalizados.
 * @returns {string} Mensagem curta para a bandeja persistente.
 */
function pvBuildSelectionStatus(pComparisonFundos = []) {
  const xSelectedCount = pComparisonFundos.length;

  if (xSelectedCount === 0) {
    return 'Adicione pelo menos 2 fundos para iniciar a comparação.';
  }

  if (xSelectedCount === 1) {
    return `1 de ${COMPARADOR_FUNDOS_MAX_ITEMS} pronto para comparação.`;
  }

  return `${xSelectedCount} de ${COMPARADOR_FUNDOS_MAX_ITEMS} prontos para comparação.`;
}

const ComparadorFundosSelectionShell = memo(
  ({
    comparisonFundos,
    ignoredSelectionNotice,
    limitNotice,
    selectionNotice,
    highlightEnabled,
    onHighlightEnabledChange,
    onAddFundo,
    onRemoveFundo,
    onReplaceFundo
  }) => {
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [targetIndex, setTargetIndex] = useState(null);
    const [shareNotice, setShareNotice] = useState('');
    const [historyItems, setHistoryItems] = useState(readComparadorFundosHistory);
    const [historyNotice, setHistoryNotice] = useState('');
    const xComparisonReady = comparisonFundos.length >= 2;
    const xVisibleSlotCount = comparisonFundos.length >= COMPARADOR_FUNDOS_MAX_ITEMS ? 3 : 2;
    const xSlots = [...comparisonFundos, ...Array(xVisibleSlotCount).fill(null)].slice(
      0,
      xVisibleSlotCount
    );
    const xTitle = xComparisonReady
      ? comparisonFundos.map(utils.helpers.getAtivoDescricao).join(' vs ')
      : 'Fundos selecionados';

    /** Abre o diálogo para preencher ou trocar o slot indicado. */
    function pvOpenDialog(pIndex) {
      setTargetIndex(pIndex);
      setDialogOpen(true);
    }

    /** Aplica a seleção no slot de destino sem substituir no limite. */
    function pvSelectFundo(pFundo) {
      if (targetIndex !== null && comparisonFundos[targetIndex]) {
        onReplaceFundo(targetIndex, pFundo);
        return;
      }

      onAddFundo(pFundo);
    }

    /** Copia a rota canônica atual e anuncia o resultado. */
    async function pvShare() {
      const xPath = buildComparadorFundosPath(
        comparisonFundos.map(pFundo => pFundo.ifeed_ativo_id)
      );
      const xUrl = new URL(xPath, window.location.origin).toString();

      try {
        await navigator.clipboard.writeText(xUrl);
        setShareNotice('Link da comparação copiado.');
      } catch (pError) {
        setShareNotice('Não foi possível copiar o link da comparação.');
      }
    }

    /** Salva explicitamente a comparação atual no histórico local. */
    function pvSaveHistory() {
      const xFundos = comparisonFundos.map(pFundo => ({
        ifeed_ativo_id: pFundo.ifeed_ativo_id,
        mercado: pFundo.mercado,
        ativo: pFundo.ativo,
        ativo_seuid: pFundo.ativo_seuid,
        ticker: pFundo.ticker
      }));
      const xNextItems = saveComparadorFundosHistoryItem(historyItems, xFundos);

      if (!writeComparadorFundosHistory(xNextItems)) {
        setHistoryNotice('Não foi possível salvar a comparação neste navegador.');
        return;
      }

      setHistoryItems(xNextItems);
      setHistoryNotice('Comparação salva no histórico.');
    }

    /** Remove somente as entradas confirmadas do histórico local. */
    function pvDeleteHistory(pKeys) {
      const xNextItems = deleteComparadorFundosHistoryItems(historyItems, pKeys);

      if (!writeComparadorFundosHistory(xNextItems)) {
        setHistoryNotice('Não foi possível atualizar o histórico neste navegador.');
        return;
      }

      setHistoryItems(xNextItems);
      setHistoryNotice(
        pKeys.length === 1
          ? 'Comparação excluída do histórico.'
          : 'Comparações excluídas do histórico.'
      );
    }

    return (
      <>
        <div id="comparador-selection-shell" />
        {xComparisonReady && (
          <Stack
            justifyContent="flex-end"
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="flex-center"
              spacing={2}
              flexWrap="wrap"
              sx={{ width: '100%', maxWidth: '100%' }}>
              {xComparisonReady ? (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  label={
                    <Typography variant="body2" color="textPrimary">
                      Destacar melhores
                    </Typography>
                  }
                  control={
                    <Switch
                      id="comparador-highlight-enabled"
                      size="small"
                      checked={highlightEnabled}
                      onChange={pEvent => onHighlightEnabledChange(pEvent.target.checked)}
                    />
                  }
                />
              ) : null}
              {xComparisonReady ? (
                <Button
                  variant="outlined"
                  onClick={pvSaveHistory}
                  size="small"
                  sx={{ px: 2 }}
                  startIcon={<Icon iconName="save" size={16} />}>
                  Salvar no histórico
                </Button>
              ) : null}
              {xComparisonReady && comparisonFundos.length < COMPARADOR_FUNDOS_MAX_ITEMS ? (
                <Button
                  variant="contained"
                  onClick={() => pvOpenDialog(comparisonFundos.length)}
                  size="small"
                  sx={{ px: 2 }}
                  startIcon={<Icon iconName="add" size={16} />}>
                  Selecionar fundo
                </Button>
              ) : null}
            </Stack>
          </Stack>
        )}

        <Stack
          direction="row"
          gap={1}
          data-comparador-header-scroll="horizontal"
          sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
          {xSlots.map((pFundo, pIndex) => (
            <Card
              key={pFundo?.ifeed_ativo_id || `slot-${pIndex}`}
              variant="outlined"
              sx={{
                flex: { xs: '0 0 240px', md: 1 },
                display: 'flex',
                flexDirection: 'column',
                border: '1px dashed',
                borderColor: 'primary.main',
                backgroundColor: 'transparent',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  borderStyle: 'solid',
                  borderColor: 'primary.main',
                  '& .MuiTypography-root': {
                    color: 'secondary.dark'
                  },
                  // Ajusta a cor dos elementos svg descendentes para 'secondary.dark'
                  '& svg': {
                    color: 'secondary.dark',
                    fill: 'currentColor',
                    stroke: 'currentColor'
                  }
                }
              }}>
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                  height: '100%',
                  width: '100%',
                  alignItems: pFundo ? 'normal' : 'center'
                }}>
                {pFundo ? (
                  <>
                    <Stack
                      direction="row"
                      gap={2}
                      onClick={() => pvOpenDialog(pIndex)}
                      sx={{
                        flex: 1,
                        alignItems: 'center'
                      }}>
                      <Stack direction="row" sx={{ flex: 1, alignItems: 'center', gap: 2 }}>
                        <PessoaImage
                          logoPessoaId={pFundo.logo_pessoa_id}
                          alt={pFundo.pessoa_fantasia}
                          hasLogo={pFundo.logo_ok}
                          size={28}
                        />
                        <Typography variant="body2">
                          {utils.helpers.getAtivoDescricao(pFundo)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <IconButton
                          type="button"
                          size="small"
                          color="error"
                          aria-label={`Remover ${utils.helpers.getAtivoDescricao(pFundo)}`}
                          onClick={pEvent => onRemoveFundo(pEvent, pFundo.ifeed_ativo_id)}>
                          <Tooltip title="Remover fundo">
                            <Box component="span" sx={{ width: '24px', height: '24px' }}>
                              <Icon iconName="remove" size={21} />
                            </Box>
                          </Tooltip>
                        </IconButton>
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <Button
                    type="button"
                    aria-label="Buscar fundo"
                    onClick={() => pvOpenDialog(pIndex)}
                    sx={{
                      width: '100%',

                      display: 'flex',
                      gap: 2,
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2
                      }
                    }}>
                    <Icon iconName="add" size={21} color="primary" />
                    <Typography variant="body2">Selecione um fundo</Typography>
                  </Button>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
        {!xComparisonReady ? (
          <ComparadorFundosHistory
            items={historyItems}
            onDelete={pvDeleteHistory}
            onView={pItem => {
              navigate(
                buildComparadorFundosPath(pItem.fundos.map(pFundo => pFundo.ifeed_ativo_id))
              );
            }}
          />
        ) : null}
        {ignoredSelectionNotice || selectionNotice || limitNotice ? (
          <Box
            role="alert"
            aria-live="assertive"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}>
            {[ignoredSelectionNotice, selectionNotice, limitNotice].filter(Boolean).join(' ')}
          </Box>
        ) : null}
        {shareNotice ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}>
            {shareNotice}
          </Box>
        ) : null}
        {historyNotice ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0
            }}>
            {historyNotice}
          </Box>
        ) : null}
        <ComparadorFundosBuscarDialog
          open={dialogOpen}
          maxSelected={COMPARADOR_FUNDOS_MAX_ITEMS}
          onClose={() => setDialogOpen(false)}
          onSelect={pvSelectFundo}
        />
      </>
    );
  }
);

export default ComparadorFundosSelectionShell;
